import React, { useEffect, useState } from 'react'
import { html } from 'lit'
import moment from 'moment'
import { useQueryClient } from "@tanstack/react-query";
import { fetchInventoryEvents, handleInventoryEvent, toastNotification, getData } from '../utils/apiFunctions'
import { inventoryEventsColumnDetails, inventoryEventFilters, InventoryEventsColumnKey, ApiEndpoints } from '../utils/common';
import { InventoryEventLabels, InventoryEventTypes, TaskStatusType} from '../utils/enums';
import { useStateContext } from '../contexts/ContextProvider';
import { HtmlEscapeUtils } from '../utils/HtmlEscapeUtils';

let triggeredOnDemandInventoryTasks = [];

const InventoryEventsGrid = () => {
    const [shouldRefreshGrid, setShouldRefreshGrid] = useState(false);
    const { finishedOnDemandInventoryTask } = useStateContext();
    const queryClient = useQueryClient();
    let gridElement = null;

    const renderDialogFooterButtons = () => {
        const footer = document.createElement('div');

        footer.innerHTML = `
          <gz-button id="resolveBtn" type="outline" color="primary">Resolve</gz-button>
          <gz-button id="cancelBtn" type="outline" color="secondary">Cancel</gz-button>
      `;
        return footer;
    }

    const formatDate = (newDate) => {
        const datetimeTitleFormat = 'MMM DD, YYYY hh:mm A';
        newDate = moment.utc(newDate).local().format(datetimeTitleFormat);
        return newDate;
    }

    // RMMI does not have the functionality to get the label for every action.
    // The labels will remain hardcoded here until the functionality is added in RMMI.
    const labels = {
        DESTINATION_GENESIS: [
            'Create',
            'Ignore'
        ],
        DESTINATION_MOVED: [
            'Move',
            'Ignore'
        ],
        DESTINATION_DELETED: [
            'Recreate',
            "'Recreate with custom name'",
            'Ignore'
        ],
        MISSING_ASSOCIATION_FOUND: [
            'Associate',
            'Rename',
            'Ignore'
        ],
        ELIGIBLE_FOR_UPGRADE: [
            'Associate',
            'Rename',
            'Ignore'
        ],
        SOURCE_DELETED: [
            'Delete',
            'Keep',
            'Ignore'
        ]
    }

    const createWindow = (wrapper, data) => {
        const renderActions = (data) => {
            let eventName = Object.keys(InventoryEventTypes)[Object.values(InventoryEventTypes).indexOf(data.eventType)]
            return data.handlingActions.map((item, index) => {
                let label = labels[eventName][index]

                return `
                    <gz-radio class='mt-3' value=${item.actionType} label=${label} fontSize="14"></gz-radio>
                    <div class='ml-3'>
                        <gz-text fontSize="13">
                                ${HtmlEscapeUtils.htmlEncode(item.actionDescription)}
                        </gz-text>
                    </div>
                    ${item.showDetails 
                        ? `<div class='mt-1 ml-3'>
                            <gz-form-field label="Action Details" id="actionDetails" stacked>
                                <gz-input
                                    gz-form-input
                                    inputWidth="240"
                                    type='text'
                                    placeholder="Provide custom name"
                                />
                                <gz-input-error></gz-input-error>
                            </gz-form-field>
                        </div>` 
                        : ''}
                    `
                    
            }).join('')
        }

        const windowBody = document.createElement('div');

        const tableHead = { header: ['Event Type', 'Type', 'Source Name', 'Destination', 'Children'], color: ['', '', '', ''] };
        const childrenData = !data.childrenEvents ? [] :
            data.childrenEvents.map(dataValue => {
                return {
                    id: dataValue.id,
                    label: InventoryEventLabels.find(i => i.value === dataValue.eventType).label,
                    value: [
                        HtmlEscapeUtils.htmlEncode(dataValue.entityTypeName),
                        HtmlEscapeUtils.htmlEncode(dataValue.rmmItemName),
                        dataValue.gravityZoneItemName ? HtmlEscapeUtils.htmlEncode(dataValue.gravityZoneItemName) : 'N/A',
                        dataValue.childrenEvents ? dataValue.childrenEvents.length : '-'
                    ]
                }
            })

        const childrenTableDataToRender = childrenData.length === 0 ?
            '<gz-text fontSize="14">None</gz-text>'
            : `<gz-table classicTableStyle head='${JSON.stringify(tableHead)}' data='${JSON.stringify(childrenData)}' />`

        
        windowBody.innerHTML = `
        <div class='ml-5 mt-10'>
            <div class='mb-2'>
                <gz-title fontSize="16" fontWeight="500">Event Details</gz-title>
            </div>
            ${HtmlEscapeUtils.htmlEncode(data.title)}
            <div class='mt-4'>
                <gz-form-field label="Source Name" id="sourceNameField" readonly>
                    <gz-input
                        gz-form-input
                        inputWidth="240"
                        type='text'
                        placeholder="N/A"
                        ${data.rmmItemName ? `value='${HtmlEscapeUtils.htmlEncode(data.rmmItemName)}'` : ''}
                    />
                    <gz-input-error></gz-input-error>
                </gz-form-field>
                <gz-form-field label="Destination Name" id="destinationNameField" readonly>
                    <gz-input
                        gz-form-input
                        inputWidth="240"
                        type='text'
                        placeholder="N/A"
                        ${data.gravityZoneItemName ? `value='${HtmlEscapeUtils.htmlEncode(data.gravityZoneItemName)}'` : ''}
                    />
                    <gz-input-error></gz-input-error>
                </gz-form-field>
            </div>
            <div class='mt-6'>
                <gz-title fontSize="14" fontWeight="500">Event description</gz-title>
            </div>
            <div class='mt-1'>
                <gz-text fontSize="14">
                    ${HtmlEscapeUtils.htmlEncode(data.description)}
                </gz-text>
            </div>
            <div class='mt-6'>
                <gz-title fontSize="16" fontWeight="500">Child Events</gz-title>
            </div>
            <div class='mt-3'>
                ${childrenTableDataToRender}
            </div>
            <div class='mt-6'>
                <gz-title fontSize="16" fontWeight="500">Actions</gz-title>
            </div>
            <div class='mt-3'>
                <gz-form-field id='actionRadioGroup'>
                    <gz-radio-group gz-form-input>
                        ${renderActions(data)}
                    </gz-radio-group>
                    <gz-input-error></gz-input-error>
                </gz-form-field>
            </div>
        `;

        const actionRadioGroup = windowBody.querySelector('#actionRadioGroup');
        const actionDetailsInput = windowBody.querySelector('#actionDetails');

        const windowFooter = renderDialogFooterButtons();
        const resolveButton = windowFooter.querySelector('#resolveBtn');
        const cancelButton = windowFooter.querySelector('#cancelBtn');

        const windowConfigs = {
            headline: 'Resolve Inventory Sync Event',
            bodyContent: windowBody,
            footerContent: windowFooter,
            isOpen: true,
            isWindow: true
        };

        const dialog = window.globalThis.nirvana.dependencies.services.buildDialogElement(windowConfigs, wrapper);

        const handleResolveEvent = async (eventType, actionValue, actionDetailsValue) => {
            let eventHandlingOptions = {
                eventHandleInfo: {
                    actionType: 0,
                    actionDetails: ''
                }
            }

            if (actionValue === undefined) {
                resolveButton.showSpinner = false;
                return;
            }

            if ((eventType === InventoryEventTypes.DESTINATION_GENESIS && parseInt(actionValue) === 2 && actionDetailsValue?.trim() === '')
                || (eventType === InventoryEventTypes.DESTINATION_DELETED && parseInt(actionValue) === 2 && actionDetailsValue?.trim() === '')) {
                toastNotification('Provide a custom name in the action details field.', 'warning', false);
                resolveButton.showSpinner = false;
                return;
            }

            eventHandlingOptions.eventHandleInfo.actionType = parseInt(actionValue)
            eventHandlingOptions.eventHandleInfo.actionDetails = actionDetailsValue || ''
            
            const taskIdentifier = await handleInventoryEvent(eventHandlingOptions, data.id);
            if (taskIdentifier) {
                triggeredOnDemandInventoryTasks.push({ taskIdentifier: taskIdentifier });
                dialog.close();
            }
        }

        if (resolveButton) {
            resolveButton.addEventListener('click', async () => {
                resolveButton.showSpinner = true;
                await handleResolveEvent(data.eventType, actionRadioGroup.value, actionDetailsInput?.value);}
            );
        }

        if (cancelButton) {
            cancelButton.addEventListener('click', () => dialog.close());
        }

        return dialog;
    }

    const defaultPageSizeOptions = [
        {
          title: '20',
          value: 20,
        },
        {
          title: '50',
          value: 50,
        },
        {
          title: '100',
          value: 100,
        },
    ];

    const defaultViewProps = {
        id: '1234',
        name: `Default Inventory Events View`,
        type: 0,
        filters: inventoryEventFilters,
        displayedColumns: [0, 1, 2, 3, 4, 5, 6],
        pageSize: 100,
        pageNumber: 1,
        compactView: false
    };

    const getDependencies = () => {
        return {
            api: {
                type: 'nirvana',
            },
            translateFn: (text, replacements) => {
                if (!replacements || !Object.keys(replacements).length) {
                    return text;
                }

                const variables = Object.keys(replacements);
                const variablesTexts = variables.map((variable) => '{' + variable + '}');
                const regex = new RegExp(variablesTexts.join('|'), 'g');

                return text.replace(regex, (matched) => {
                    return replacements[matched.slice(1, -1)];
                });
            },
            getTimezone: () => 'UTC',
            getUserLanguage: () => 'en-US',
        };
    }

    class DataProvider {

        dependencies;
        allColumnDetails;
        targetIds;
        defaultViewProps;
        hasPagination;

        constructor(dependencies, allColumnDetails, targetIds, defaultTargetIds, hasPagination) {
            this.dependencies = dependencies;
            this.allColumnDetails = allColumnDetails;
            this.targetIds = targetIds;
            this.defaultTargetIds = defaultTargetIds;
            this.hasPagination = hasPagination;
        }

        async getSummary(request) {
            const totalInventoryEvents = await fetchInventoryEvents(request.filters);

            return new Promise(resolve => {
                setTimeout(() => {
                    resolve({
                        summary: `${totalInventoryEvents.length} items`,
                        numItems: totalInventoryEvents.length,
                    });
                }, 300);
            });
        }

        async getRows(request) {
            const inventoryEvents = await fetchInventoryEvents(request.filters, request.pageNumber, request.pageSize);

            return new Promise(resolve => {
                setTimeout(() => {
                    resolve({
                        rawEvents: inventoryEvents.map((x) => {
                            return {
                                eventId: x["id"],
                                eventType: x["eventType"],
                                eventTypeName: x["eventTypeName"],
                                entityType: x["entityType"],
                                entityTypeName: x["entityTypeName"],
                                rmmItemName: x["rmmItemName"],
                                gravityZoneItemName: x["gravityZoneItemName"],
                                childrenCount: x["childrenEvents"]?.length,
                                title: x["title"],
                                description: x["description"],
                                handlingActions: x["handlingActions"],
                                childrenEvents: x["childrenEvents"],
                                createdDate: formatDate(x["createdDate"])
                            }
                        }),
                    });
                }, 300);
            });
        }

        async updateSettings() {
            return {};
        }

        async setFilters() {
            return {};
        }

        shouldDataReloadOnResetView() {
            return true;
        }
    };


    const dependencies = getDependencies();

    const handleResolve = async (rawData) => {
        const body = document.getElementById('inventoryEvents-container');
        const event = await getData(`${ApiEndpoints.INVENTORY_EVENTS}/${rawData.item.eventId}`);
        const dialog = createWindow(body, event);

        const resolveBton = dialog.shadowRoot.querySelector('#resolveBtn');
        if (resolveBton) {
            resolveBton.addEventListener('click', handleResolve);
        }
    }

    const getColumnRenderers = {
        [InventoryEventsColumnKey.EVENT_TYPE]: rowData => {
            return html`<gz-grid-cell-default-renderer
        .label="${HtmlEscapeUtils.htmlEncode(rowData?.item.eventTypeName)}"
      ></gz-grid-cell-default-renderer>`;
        },
        [InventoryEventsColumnKey.TYPE]: rowData => {
            return html`<gz-grid-cell-default-renderer
        .label="${HtmlEscapeUtils.htmlEncode(rowData?.item.entityTypeName)}"
      ></gz-grid-cell-default-renderer>`;
        },
        [InventoryEventsColumnKey.SOURCE_NAME]: rowData => {
            return html`<gz-grid-cell-default-renderer
        .label="${HtmlEscapeUtils.htmlEncode(rowData?.item.rmmItemName)}"
      ></gz-grid-cell-default-renderer>`;
        },
        [InventoryEventsColumnKey.DESTINATION_NAME]: rowData => {
            return html`<gz-grid-cell-default-renderer
        .label="${HtmlEscapeUtils.htmlEncode(rowData?.item.gravityZoneItemName)}"
      ></gz-grid-cell-default-renderer>`;
        },
        [InventoryEventsColumnKey.CHILDREN_COUNT]: rowData => {
            return html`<gz-grid-cell-default-renderer
        .label="${HtmlEscapeUtils.htmlEncode(rowData?.item.childrenCount)}"
      ></gz-grid-cell-default-renderer>`;
        },
        [InventoryEventsColumnKey.GENERATED_AT]: rowData => {
            return html`<gz-grid-cell-default-renderer
        .label="${HtmlEscapeUtils.htmlEncode(rowData?.item.createdDate)}"
      ></gz-grid-cell-default-renderer>`;
        },
        [InventoryEventsColumnKey.ACTIONS]: rowData => {
            return html`
            <gz-button id="resolveEventBtn" type="outline"  @click="${handleResolve.bind(this, rowData)}" color="primary">Resolve</gz-button>
            `;
        },
    }

    const provider = new DataProvider(dependencies, inventoryEventsColumnDetails, [], [], true);

    useEffect(() => {
        gridElement = document.getElementById('inventoryEventsGrid');

        gridElement.dataProvider = provider
        gridElement.titleConfig = JSON.stringify({ title: 'Inventory Events' })
        gridElement.viewId = '1234'
        gridElement.allColumnDetails = inventoryEventsColumnDetails
        gridElement.defaultViewProps = defaultViewProps
        gridElement.pageSizeOptions = defaultPageSizeOptions
        gridElement.isCompactViewChecked = false
        gridElement.columns = [0, 1, 2, 3, 4, 5, 6]
        gridElement.predefinedFiltersKeys = [InventoryEventsColumnKey.EVENT_TYPE, InventoryEventsColumnKey.TYPE, InventoryEventsColumnKey.SOURCE_NAME, InventoryEventsColumnKey.DESTINATION_NAME]
        gridElement.filters = inventoryEventFilters
        gridElement.pageSize = 100
        gridElement.hasPagination = true
        gridElement.columnRenderers = getColumnRenderers
        gridElement.enableRowClickEvent = false;
        gridElement.propertiesSet = true

        gridElement.injectDependencies(dependencies);
        gridElement.start().then().catch(console.error);
    }, [shouldRefreshGrid]);

    useEffect(() => {
        let triggeredOnDemandInventoryTask = triggeredOnDemandInventoryTasks.find(({ taskIdentifier }) => taskIdentifier === finishedOnDemandInventoryTask.taskIdentifier);
        if (typeof triggeredOnDemandInventoryTask === 'undefined' || triggeredOnDemandInventoryTask === null) {
            return;
        }
        if (finishedOnDemandInventoryTask.taskStatusType === TaskStatusType.SUCCEEDED) {
            queryClient.invalidateQueries({ queryKey: ["inventoryWithAssociations"] });
            queryClient.invalidateQueries({ queryKey: ["fetchOrgs"] });
            setShouldRefreshGrid(shouldRefreshGrid => !shouldRefreshGrid);
        }
        triggeredOnDemandInventoryTasks = triggeredOnDemandInventoryTasks.filter(t => t.taskIdentifier !== finishedOnDemandInventoryTask.taskIdentifier);
    }, [finishedOnDemandInventoryTask]);

    window.nirvana.translator = {
        translateFn: (x) => x
    };

    return (
        <gz-grid-view style={{ "height": "87vh" }}
            id="inventoryEventsGrid"
            disableGridSettings>
        </gz-grid-view>
    )
}

export default InventoryEventsGrid